import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { FormHelperText, lighten, Typography, darken } from '@mui/material'
import Box from '@mui/material/Box'
import Checkbox from '@mui/material/Checkbox'
import Fade from '@mui/material/Fade'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormLabel from '@mui/material/FormLabel'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import TextField from '@mui/material/TextField'
import {
  CustomField,
  CustomFieldValues,
} from '@promoboxx/graphql-gateway-types'
import { getIn, FieldProps } from 'formik'
import I18n from 'i18n-js'
import { useRef } from 'react'
import { makeStyles } from 'tss-react/mui'

import MenuItemWithCheckbox from '@src/lib/MenuItemWithCheckbox'

import { useManageBrandDialogContext } from './manageBrandsDialogContext'
import useTempBooleanState from './useTempBooleanState'

export interface CustomFieldFieldProps extends FieldProps {
  customField: CustomField
  submitField: (config: {
    customFieldId: CustomField['id']
    customFieldValueId?: CustomFieldValues['id']
    value: string
  }) => Promise<CustomFieldValues | null | undefined>
}

const useStyles = makeStyles()((theme) => ({
  missingConnection: {
    color: darken(theme.palette.warning.contrastText, 0.9),
    // Copying what Alert does.
    // https://github.com/mui/material-ui/blob/a563a60219f7f6519fb0f34f6d8e3bf0974e6495/packages/material-ui-lab/src/Alert/Alert.js#L46
    backgroundColor: lighten(theme.palette.warning.main, 0.9),
  },
  checkBoxOrRadio: {
    padding: theme.spacing(0, 2),
    borderRadius: theme.shape.borderRadius,
  },
}))

const CustomFieldField: React.FC<
  React.PropsWithChildren<CustomFieldFieldProps>
> = (props) => {
  const { classes, cx } = useStyles()
  const [tempSuccess, setTempSuccess] = useTempBooleanState()
  const { field, form, submitField } = props
  const { isSubmitting } = form
  const fieldError = getIn(form.errors, field.name)
  const showError = getIn(form.touched, field.name) && !!fieldError

  const { shouldHighlightMissing } = useManageBrandDialogContext()
  const customField: CustomField | undefined = field.value
  const customFieldValue = customField?.customFieldValues?.value
  const previousValueRef = useRef(customFieldValue)

  // `field.value` can be undefined when going through a brandId change.
  // It will sort itself out soon enough, as this happens when query is already
  // cached.
  if (!customField) {
    return null
  }

  const updateField = async (value: string) => {
    field.onChange(`${field.name}.customFieldValues.value`)(value)

    // Only make a request if the user actually changed the value, not just
    // clicked in a text field and clicked outside of it.
    if (previousValueRef.current !== value) {
      try {
        const data = await submitField({
          customFieldId: customField.id,
          customFieldValueId: customField.customFieldValues?.id,
          value,
        })

        if (data) {
          form.setFieldValue(field.name, {
            ...customField,
            customFieldValues: data,
          })
        }

        setTempSuccess()
      } catch (e) {
        console.error(e)
      }
    }

    previousValueRef.current = value
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateField(e.target.value)
  }

  if (customField.input_type === 'boolean') {
    // A checkbox custom field requires a "true" or "false" string value so the
    // change handler is slightly different.
    const handleCheckboxChange = (
      event: React.ChangeEvent<HTMLInputElement>,
      checked: boolean,
    ) => {
      updateField(checked ? 'true' : 'false')
    }

    const shouldHighlight = Boolean(
      shouldHighlightMissing &&
        (!customFieldValue || customFieldValue === 'false') &&
        customField.required,
    )

    return (
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <div
          className={cx({
            [classes.missingConnection]: shouldHighlight,
            [classes.checkBoxOrRadio]: shouldHighlight,
          })}
        >
          <FormControl error={shouldHighlight}>
            <FormLabel component="legend" required={!!customField.required}>
              {customField.display_name}
            </FormLabel>
            <FormControlLabel
              control={
                <Checkbox
                  {...field}
                  checked={customFieldValue === 'true'}
                  onChange={handleCheckboxChange}
                  color="primary"
                  disabled={
                    customField.retailer_permission !== 'write' || isSubmitting
                  }
                />
              }
              label={customField.display_name}
            />
          </FormControl>
          {showError && <FormHelperText error>{fieldError}</FormHelperText>}
        </div>
        <SaveFade tempSuccess={tempSuccess} />
      </Box>
    )
  }

  if (customField.input_type === 'select') {
    const choices = customField?.choices?.split('\r\n')
    const shouldHighlight = Boolean(
      shouldHighlightMissing && !customFieldValue && customField.required,
    )
    return (
      <Box display="flex" alignItems="center">
        <TextField
          InputProps={{
            className: cx({
              [classes.missingConnection]: shouldHighlight,
            }),
          }}
          {...field}
          value={customFieldValue}
          select
          error={showError || shouldHighlight}
          fullWidth
          helperText={fieldError}
          label={customField.display_name}
          disabled={customField.retailer_permission !== 'write' || isSubmitting}
          onChange={handleChange}
          required={!!customField.required}
        >
          {choices?.map((choice) => (
            <MenuItemWithCheckbox value={choice} key={choice}>
              {choice}
            </MenuItemWithCheckbox>
          ))}
        </TextField>
        <SaveFade tempSuccess={tempSuccess} />
      </Box>
    )
  }

  if (
    customField.input_type === 'text' ||
    customField.input_type === 'textarea'
  ) {
    const shouldHighlight = Boolean(
      shouldHighlightMissing && !customFieldValue && customField.required,
    )
    return (
      <Box borderRadius={1} display="flex" alignItems="center">
        <TextField
          {...field}
          InputProps={{
            className: cx({
              [classes.missingConnection]: shouldHighlight,
            }),
          }}
          value={customFieldValue}
          error={showError || shouldHighlight}
          fullWidth
          helperText={fieldError}
          multiline={customField.input_type === 'textarea'}
          rows={5}
          label={customField.display_name}
          onBlur={(e) => {
            field.onBlur(e)
            updateField(e.target.value)
          }}
          onChange={(event) => {
            // We still need to run an onChange so the value updates as the user
            // types. Due to the shape of the custom field it's not as simple as
            // just `setField(field.name, value)`.
            field.onChange(`${field.name}.customFieldValues.value`)(
              event.target.value,
            )
          }}
          disabled={customField.retailer_permission !== 'write' || isSubmitting}
          required={!!customField.required}
        />
        <SaveFade tempSuccess={tempSuccess} />
      </Box>
    )
  }

  if (customField.input_type === 'radio') {
    const choices = customField?.choices?.split('\r\n')
    const shouldHighlight = Boolean(
      shouldHighlightMissing && !customFieldValue && customField.required,
    )
    return (
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <div
          className={cx({
            [classes.missingConnection]: shouldHighlight,
            [classes.checkBoxOrRadio]: shouldHighlight,
          })}
        >
          <FormControl component="fieldset" error={shouldHighlight}>
            <FormLabel component="legend" required={!!customField.required}>
              {customField.display_name}
            </FormLabel>
            <RadioGroup
              {...field}
              value={customFieldValue}
              onChange={handleChange}
            >
              {choices?.map((choice) => (
                <FormControlLabel
                  key={choice}
                  value={choice}
                  control={<Radio />}
                  label={choice}
                  disabled={
                    customField.retailer_permission !== 'write' || isSubmitting
                  }
                />
              ))}
            </RadioGroup>
            {showError && <FormHelperText error>{fieldError}</FormHelperText>}
          </FormControl>
        </div>
        <SaveFade tempSuccess={tempSuccess} />
      </Box>
    )
  }

  return null
}

const SaveFade = ({ tempSuccess }: { tempSuccess: boolean }) => (
  <Fade in={tempSuccess}>
    <Box textAlign="center" mx={4}>
      <CheckCircleOutlineIcon style={{ color: '#4BB543' }} />
      <Typography
        variant="caption"
        style={{ color: '#4BB543' }}
        component="div"
      >
        {I18n.t('manageBrands.saved')}
      </Typography>
    </Box>
  </Fade>
)

export default CustomFieldField
